import * as React from 'react'

import {
  Avatar,
  Box,
  Card,
  Icon,
  Link,
  StatusLabel,
  StatusLabelTypes,
  Tag,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { Status } from 'modules/ats/lib/enums'
import { Promotions } from 'modules/ats/store/collections/promotions'
import type Job from 'modules/ats/store/models/Job'

export interface ProviderComponentProps {
  job: Job
  promotions: Promotions
}

interface Props {
  logo: string
  title: string
  description: string
  action: React.ReactNode
  secondaryAction?: React.ReactNode
  websiteUrl?: string
  jobExternalUrl?: string
  status?: Status
  isFree?: boolean
  error?: string
  children?: React.ReactNode
}

const JobBoardCard = ({
  logo,
  title,
  description,
  action,
  secondaryAction,
  websiteUrl,
  jobExternalUrl,
  status,
  isFree = false,
  error,
  children,
}: Props) => {
  const [open, toggleOpen] = useToggle()
  const showWebsiteButton =
    !!websiteUrl && (!status || status === Status.Linked || status === Status.Published)

  const jobUrl = status === Status.Error ? undefined : jobExternalUrl

  const statusToTagColor: Record<Status, StatusLabelTypes> = {
    linked: 'warning',
    in_progress: 'info',
    live: 'info',
    offline: 'neutral',
    published: 'positive',
    action_required: 'critical',
    expired: 'critical',
    error: 'critical',
  }

  const statusLabel: Record<Status, string> = {
    linked: i18n.t('ats.job_details.promotion.job_boards.status.linked'),
    in_progress: i18n.t('ats.job_details.promotion.job_boards.status.in_progress'),
    live: i18n.t('ats.job_details.promotion.job_boards.status.live'),
    offline: i18n.t('ats.job_details.promotion.job_boards.status.offline'),
    published: i18n.t('ats.job_details.promotion.job_boards.status.published'),
    action_required: i18n.t('ats.job_details.promotion.job_boards.status.action_required'),
    expired: i18n.t('ats.job_details.promotion.job_boards.status.expired'),
    error: i18n.t('ats.job_details.promotion.job_boards.status.error'),
  }

  return (
    <Card>
      <Card.Eyebrow>
        <>
          <Avatar label={title} url={logo} />
          {!!status && (
            <StatusLabel type={statusToTagColor[status]}>{statusLabel[status]}</StatusLabel>
          )}
        </>
      </Card.Eyebrow>
      <Card.Title>{title}</Card.Title>
      <Card.Description>{description}</Card.Description>
      <Card.Content>
        <Box justifyContent="flexEnd" height="full">
          <Box flexDirection="row" gap="s12" paddingTop="s12" alignItems="center">
            {action}
            {secondaryAction}
            {showWebsiteButton && (
              <Link.Href href={websiteUrl} target="_blank" type="default" external>
                {i18n.t('ats.job_details.promotion.job_boards.buttons.website')}
              </Link.Href>
            )}
            {jobUrl && (
              <Link.Href href={jobUrl} target="_blank" type="default" external>
                {i18n.t('common.view')}
              </Link.Href>
            )}
            {isFree && (
              <Box>
                <Tag type="lime">{i18n.t('ats.job_details.promotion.job_boards.free_tag')}</Tag>
              </Box>
            )}
            {error && (
              <Tooltip.Alt open={open} toggle={toggleOpen} label={error} attachment="bottom center">
                <Icon.Medium icon="info-circle-line" color="radicalRed1000" />
              </Tooltip.Alt>
            )}
          </Box>
          {children}
        </Box>
      </Card.Content>
    </Card>
  )
}

export default JobBoardCard
